import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg, useApi } from "@/lib/api";
import { fmtDate } from "@/console/util";

const Field = ({ label, hint, value, onChange, testid, prefix }) => (
  <label className="block">
    <span className="small-label">{label}</span>
    <div className="relative mt-2">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input type="number" min="0" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`rounded-sm bg-white num ${prefix ? "pl-7" : ""}`} data-testid={testid} />
    </div>
    {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
  </label>
);

export default function ConsolePricing() {
  const api = useApi();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sa/pricing").then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api]);

  const num = (v) => (v === "" || v == null || Number(v) <= 0 ? null : Number(v));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/sa/pricing", { monthly_price: num(p.monthly_price), yearly_price: num(p.yearly_price), trial_days: Number(p.trial_days || 0) });
      setP(r.data);
      toast.success("Prices published");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  if (!p) return <p className="small-label">Loading…</p>;
  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Pricing</h1>
      <p className="text-slate-500 mt-2 text-sm">One flat price per firm, in US dollars. Leave a price empty to hide that plan. New prices apply to new subscriptions; existing subscribers keep the price they signed up at.</p>
      <div className="panel p-6 mt-8 grid sm:grid-cols-3 gap-6">
        <Field label="Monthly price" prefix="$" value={p.monthly_price} onChange={(v) => setP({ ...p, monthly_price: v })} testid="pricing-monthly-input" />
        <Field label="Yearly price" prefix="$" value={p.yearly_price} onChange={(v) => setP({ ...p, yearly_price: v })} testid="pricing-yearly-input" />
        <Field label="Free trial days" hint="0 = no trial" value={p.trial_days} onChange={(v) => setP({ ...p, trial_days: v })} testid="pricing-trial-input" />
      </div>
      <div className="flex items-center gap-4 mt-4">
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="pricing-save-button">{saving ? "Publishing…" : "Publish prices"}</Button>
        {p.updated_at && <span className="text-xs text-slate-500">Last updated {fmtDate(p.updated_at)}</span>}
      </div>
    </div>
  );
}
